import {encodeCompanyId} from '../featureFlagRows';

/**
 * A cell holds a company id only if it is entirely digits.
 *
 * Tested rather than parsed on purpose: `Number.parseInt('42a')` is `42`, so parsing would grant the
 * flag to company 42 on the strength of a typo. Anything that is not a plain non-negative integer is
 * reported back to the operator instead.
 */
const COMPANY_ID_PATTERN = /^\d+$/;

/** Header names that look like a company id column, in the order they are preferred. */
const COMPANY_ID_HEADERS = ['companyid', 'company', 'id'];

const normalizeHeader = (header: string): string => header.toLowerCase().replace(/[^a-z0-9]/g, '');

export interface RolloutCsvResolution {
	/** Encoded ids of the companies to add, in file order, each listed once. */
	matchedCompanyIds: string[];
	/** Raw cell values that named no company, in file order, each listed once. */
	unmatchedValues: string[];
	/** Names of companies in the file that already hold the flag, in file order, each listed once. */
	alreadyGrantedNames: string[];
}

export interface RolloutCsvResolutionInput {
	/** Parsed file. Row 0 is the header and is never resolved. Rows may be ragged. */
	rows: readonly string[][];
	/** Which column holds the company id. Out of range for every row resolves nothing. */
	columnIndex: number;
	/** Encoded company id to name — the rollout directory's own map. */
	companyNames: Map<string, string>;
	/**
	 * Every company already on the flag, eligible or not — including one holding it through named
	 * users only. Passing the full set is what keeps a CSV from silently widening a hand-configured
	 * named-users company into a grant for everyone there.
	 */
	grantedCompanyIds: Set<string>;
}

/**
 * The companies a CSV column names, split by what can be done with each.
 *
 * A cell is resolved by trimming it, requiring a plain integer, and encoding that as a company id.
 * Blank cells are ignored rather than reported: an empty cell is noise, not operator error.
 *
 * Order is file order in every bucket, never re-sorted, so the preview reads down alongside the
 * operator's own spreadsheet. Companies outside the eligible pool are matched like any other; they
 * simply do not move the rollout percentage.
 */
export function resolveCsvCompanies({
	rows,
	columnIndex,
	companyNames,
	grantedCompanyIds,
}: RolloutCsvResolutionInput): RolloutCsvResolution {
	const matchedCompanyIds: string[] = [];
	const unmatchedValues: string[] = [];
	const alreadyGrantedNames: string[] = [];
	const seenCompanyIds = new Set<string>();
	const seenUnmatchedValues = new Set<string>();

	rows.slice(1).forEach(row => {
		const cell = (row[columnIndex] ?? '').trim();
		if (cell === '') {
			return;
		}

		const companyId = COMPANY_ID_PATTERN.test(cell) ? encodeCompanyId(Number(cell)) : null;
		if (companyId == null || !companyNames.has(companyId)) {
			if (!seenUnmatchedValues.has(cell)) {
				seenUnmatchedValues.add(cell);
				unmatchedValues.push(cell);
			}
			return;
		}

		if (seenCompanyIds.has(companyId)) {
			return;
		}
		seenCompanyIds.add(companyId);

		if (grantedCompanyIds.has(companyId)) {
			// Falls back to the normalized numeric id for a company the directory has no name for — the
			// same fallback the preview table itself uses, so a leading zero in the cell (`0043`) cannot
			// read as one company in the skip strip and a different one in the table.
			alreadyGrantedNames.push(companyNames.get(companyId) || `Company ${Number(cell)}`);
			return;
		}
		matchedCompanyIds.push(companyId);
	});

	return {matchedCompanyIds, unmatchedValues, alreadyGrantedNames};
}

/**
 * The column a freshly loaded file starts on.
 *
 * Each candidate name is looked for across the whole header row before the next is tried, so an
 * exact `company_id` late in the file beats a bare `id` early in it. Headers are compared with case
 * and punctuation stripped, so `Company ID` and `company_id` both match. Falls back to the first
 * column when no header looks like a company id.
 */
export function selectCompanyIdColumn(header: readonly string[]): number {
	const normalized = header.map(normalizeHeader);
	for (let candidate = 0; candidate < COMPANY_ID_HEADERS.length; candidate += 1) {
		const index = normalized.indexOf(COMPANY_ID_HEADERS[candidate]);
		if (index !== -1) {
			return index;
		}
	}
	return 0;
}
